import type { GridLayer, PresentationTaskRunner, SymbolView } from "@slot/presentation";
import type { SymbolMergeOutput } from "./MergeSymbolsActionHandler";
import type { MergeTuning } from "./MergeTuning";

/** What `SymbolMerge` tells its listener as each target turns into its result (a title's sparkle, its ladder's pulse). */
export interface MergeLanding {
  targetPositionIndex: number;
  resultSymbolId: string;
}

/**
 * The merge's motion over a `GridLayer`: every output at once (a substep's clusters merge together), each after the
 * stagger of its place in the action. The output's consumed symbols fly from their cells into the target's, shrinking
 * as they go, drawn over the grid; as they arrive they leave the grid (released, the cells empty for the collapse) and
 * the target pops — it swells, becomes the result symbol at the peak (the same view, rebound: it keeps its cell and
 * falls with the survivors), and settles. Runs under `merge_motion` on the presentation runner (engine time: the speed
 * modes scale it); with no runner every output resolves at once (headless tests).
 */
export class SymbolMerge {
  /** The merge's own scope. No mechanic declares it as a slam scope: a slam never shortens a merge. */
  public static readonly SCOPE = "merge_motion";

  public constructor(
    private readonly deps: {
      grid: GridLayer;
      taskRunner: PresentationTaskRunner | null;
      tuning: MergeTuning;
      /** Fired as each target becomes its result, at the pop's peak. */
      onLanding?: (landing: MergeLanding) => void;
    },
  ) {}

  /** Plays every output; resolves when the last target has settled. Each output's cells must be bound (the presenter checks). */
  public async merge(outputs: readonly SymbolMergeOutput[]): Promise<void> {
    const runner = this.deps.taskRunner;
    if (!runner) {
      for (const output of outputs) {
        for (const index of output.consumedPositionIndices) this.deps.grid.releaseAt(index, "merge_consume");
        this.becomeResult(output, this.view(output.targetPositionIndex));
      }
      return;
    }
    await runner.runInScope(SymbolMerge.SCOPE, () => Promise.all(outputs.map((output, order) => this.play(runner, output, order * this.deps.tuning.outputStaggerSeconds))).then(() => undefined));
  }

  private async play(runner: PresentationTaskRunner, output: SymbolMergeOutput, delaySeconds: number): Promise<void> {
    const { grid, tuning } = this.deps;
    const scope = SymbolMerge.SCOPE;
    const target = this.view(output.targetPositionIndex);
    const landing = grid.getCellLocalPosition(output.targetPositionIndex);
    if (delaySeconds > 0) await runner.sleep(delaySeconds, scope);

    await Promise.all(
      output.consumedPositionIndices.map(async (index) => {
        const node = this.view(index).node;
        // Over the target and every other cell while it flies (the pool never relies on its children's order).
        node.parent?.addChild(node);
        await runner.tweenTo(node, tuning.flySeconds, { position: landing, scale: { x: tuning.consumedEndScale, y: tuning.consumedEndScale } }, scope, { easing: tuning.flyEasing });
        grid.releaseAt(index, "merge_consume");
      }),
    );

    const half = tuning.popSeconds / 2;
    await runner.tweenTo(target.node, half, { scale: { x: tuning.popScale, y: tuning.popScale } }, scope, { easing: "quadOut" });
    this.becomeResult(output, target);
    await runner.tweenTo(target.node, half, { scale: { x: 1, y: 1 } }, scope, { easing: "backOut" });
  }

  private becomeResult(output: SymbolMergeOutput, target: SymbolView): void {
    this.deps.grid.pool.applySymbolIdToView(target, output.resultSymbolId);
    this.deps.onLanding?.({ targetPositionIndex: output.targetPositionIndex, resultSymbolId: output.resultSymbolId });
  }

  private view(index: number): SymbolView {
    const view = this.deps.grid.getSymbolViewAtIndex(index);
    if (!view) throw new Error(`SymbolMerge: no symbol at cell ${index}`);
    return view;
  }
}
